/* eslint-disable react/button-has-type,react/prop-types */
import type { ButtonHTMLAttributes } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { BadgeSize } from '../../components/Badge/Badge';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import type { CSS } from '../../stitches.config';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { Breakpoint } from '../../utilities/useViewport';
import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariant,
  ControlWidth,
} from '../shared/types';
import { Icon } from '../../assets/Icon/Icon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { Badge } from '../../components/Badge/Badge';
import { type TooltipSide, Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import Shortcut from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import {
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { Callout } from '../Callout/Callout';
import {
  useControlAppearance,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlArrowsSize,
  controlBorderRadius,
  controlGap,
  controlHeight,
  controlIconSize,
  controlLoadingSize,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlPreset,
} from '../shared/vars';
import {
  destructiveMutedDisabledStyles,
  destructiveMutedEnabledStyles,
  destructiveVividDisabledStyles,
  destructiveVividEnabledStyles,
  getInteriorVariantStyles,
  iconColor,
  internalDisabledStyles,
  internalEnabledStyles,
  labelColor,
  loadingIconColor,
  primaryMutedDisabledStyles,
  primaryMutedEnabledStyles,
  primaryVividDisabledStyles,
  primaryVividEnabledStyles,
  secondaryMutedDisabledStyles,
  secondaryMutedEnabledStyles,
  secondaryVividDisabledStyles,
  secondaryVividEnabledStyles,
} from './Button.styles';

const ButtonIcon = styled(Icon, {
  opacity: 1,
  size: controlIconSize,
  color: iconColor,
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
  },
});
ButtonIcon.displayName = 'ButtonIcon';

const ButtonMenuArrow = styled(ButtonIcon, {
  size: controlArrowsSize,
});
ButtonMenuArrow.displayName = 'ButtonMenuArrow';

const ButtonShortcut = styled(Shortcut, {
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
  },
});
ButtonShortcut.displayName = 'ButtonShortcut';

const ButtonLoadingIcon = styled(LoadingIcon, {
  size: controlLoadingSize,
  fill: loadingIconColor,
  transition: transitions.control,

  circle: {
    fill: loadingIconColor,
  },
});
ButtonLoadingIcon.displayName = 'ButtonLoadingIcon';

const ButtonLabel = styled(Text, {
  display: 'flex',
  color: labelColor,
  fontWeight: '$bold',
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    arrangement: {
      'leading-icon': {},
      'leading-label': {},
    },
    condense: {
      true: {},
      false: {},
    },
    stretch: {
      true: {
        width: '100%',
      },
      false: {},
    },
  },
});
ButtonLabel.displayName = 'ButtonLabel';

export const ButtonContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: controlGap,
  heightAll: controlHeight,
  minWidth: 'min-content',
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  opacity: 1,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    appearance: {
      muted: {},
      vivid: {},
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {},
      'leading-label': {},
    },
    hasMenu: {
      true: {},
      false: {},
    },
    ...controlVars,
    truncate: {
      true: {
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    ghost: {
      true: {},
      false: {},
    },
    invalid: {
      true: {},
      false: {},
    },
    variant: {
      destructive: {},
      primary: {},
      secondary: {},
    },
  },
  compoundVariants: [
    // Icon only
    {
      arrangement: 'hidden-label',
      size: 'x-small',
      hasMenu: false,
      css: {
        minWidth: '$24',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'small',
      hasMenu: false,
      css: {
        minWidth: '$28',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'medium',
      hasMenu: false,
      css: {
        minWidth: '$32',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'large',
      hasMenu: false,
      css: {
        minWidth: '$40',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'x-large',
      hasMenu: false,
      css: {
        minWidth: '$48',
      },
    },
    // Destructive
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: false,
      css: {
        '&[type=button], &[type=submit]': destructiveVividEnabledStyles,
        '&': destructiveVividEnabledStyles,
      },
    },
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveVividDisabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: false,
      css: destructiveMutedEnabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveMutedDisabledStyles,
    },

    // Primary
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: false,
      css: {
        '&[type=button], &[type=submit]': primaryVividEnabledStyles,
        '&': primaryVividEnabledStyles,
      },
    },
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: true,
      css: primaryVividDisabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: false,
      css: primaryMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: true,
      css: primaryMutedDisabledStyles,
    },

    // Secondary vivid
    {
      appearance: 'vivid',
      variant: 'secondary',
      isDisabled: false,
      css: secondaryVividEnabledStyles,
    },
    {
      appearance: 'vivid',
      variant: 'secondary',
      isDisabled: true,
      css: secondaryVividDisabledStyles,
    },

    // Secondary muted
    {
      appearance: 'muted',
      variant: 'secondary',
      isDisabled: false,
      css: secondaryMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'secondary',
      isDisabled: true,
      css: secondaryMutedDisabledStyles,
    },

    // Internal
    {
      internal: true,
      isDisabled: false,
      css: internalEnabledStyles,
    },
    {
      internal: true,
      isDisabled: true,
      css: internalDisabledStyles,
    },

    {
      isDisabled: false,
      invalid: true,
      css: {
        boxShadow: shadows.fieldErrorLight,

        '&:hover:not(:focus)': {
          boxShadow: shadows.fieldErrorLight,
        },

        [darkThemeSelector]: {
          boxShadow: shadows.fieldErrorDark,

          '&:hover:not(:focus)': {
            boxShadow: shadows.fieldErrorDark,
          },
        },
      },
    },

    // Ghost
    {
      ghost: true,
      css: {
        '&[data-ghost="true"]:not(:hover, :focus, :focus-visible, :focus-within)': {
          backgroundColor: colors.transparent,
          boxShadow: colors.transparent,
          outlineColor: colors.transparent,
        },
      },
    },
  ],
});
ButtonContainer.displayName = 'ButtonContainer';

const ButtonLoadingContainer = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
ButtonLoadingContainer.displayName = 'ButtonLoadingContainer';

export type ButtonAppearanceProp = ControlAppearance;
export type ButtonArrangementProp = 'hidden-label' | 'leading-icon' | 'leading-label';
export type ButtonMenuArrowProp = 'dropdown' | 'select';
export type ButtonLabelAlignProp = 'start' | 'center' | 'end';
export type ButtonSizeProp = ControlSize;
export type ButtonTypeProp = 'button' | 'submit' | 'reset';
export type ButtonFixturesProp = VariantProp;
export type ButtonVariantProp = ControlVariant;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Additional annotation such a result count within the button.
   * */
  annotation?: React.ReactNode;
  /**
   * The level required to access the button.
   */
  accessLevel?: AccessLevel;
  /**
   * Determines if the button is muted (more flat, simple colors) or vivid (more pronounced, colorful).
   */
  appearance?: ButtonAppearanceProp;
  /**
   * Determines the layout of the button's content.
   */
  arrangement?: ButtonArrangementProp;
  /**
   * Show a callout dot in the button to indicate a change/update behind route.
   */
  callout?: VariantProp;
  /**
   * The content of the button.
   */
  children: React.ReactNode;
  /**
   * Hides the button's label for smaller viewports.
   */
  condense?: boolean;
  /**
   * Disables the button.
   */
  disabled?: boolean;
  /**
   * Marks the button as invalid.
   */
  invalid?: boolean;
  /**
   * Set the form attribute.
   */
  form?: string;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Marks the button as internal.
   */
  internal?: boolean;
  /**
   * Sets the variant for the button's internal fixtures (icon, label, etc.) when `variant` is `secondary`.
   */
  fixtures?: ButtonFixturesProp;
  /**
   * Hides the button's background and stroke until interacted with.
   */
  ghost?: boolean;
  /**
   * Displays a dropdown arrow for menus.
   */
  menuArrow?: ButtonMenuArrowProp;
  /**
   * Set the alignment of the button's label.
   */
  labelAlign?: ButtonLabelAlignProp;
  /**
   * Stretches the button's label to fill the width of the button.
   */
  labelStretch?: boolean;
  /**
   * Displays a loading icon.
   */
  loading?: boolean;
  /**
   * Set shortcut keys for the button.
   */
  shortcut?: ShortcutKey[];
  /**
   * Shorter label for the button.
   */
  shortLabel?: React.ReactNode;
  /**
   * Set the size of the button.
   */
  size?: ButtonSizeProp;
  /**
   * Truncate the button label.
   */
  truncate?: boolean;
  /**
   * If the button label is shown as a tooltip, the preferred side of the trigger to render against when open.
   */
  tooltipSide?: TooltipSide;
  /**
   * Set the type of the button.
   */
  type?: ButtonTypeProp;
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: ButtonVariantProp;
  /**
   * Set a width.
   */
  width?: ControlWidth;
  /**
   * Set a maximum width.
   */
  maxWidth?: ControlMaxWidth;
  /**
   * Set a minimum width.
   */
  minWidth?: ControlMinWidth;
}

function getButtonGap(size: ButtonSizeProp, breakpoint: Breakpoint): SpacingScale | undefined {
  switch (size) {
    case 'x-small':
      return 2;
    case 'small':
    case 'medium':
      return breakpoint === 'desktop' ? 4 : 6;
    case 'large':
      return 6;
    case 'x-large':
      return 8;
  }

  return undefined;
}

export function Button<Tag extends React.ElementType>({
  annotation,
  appearance,
  'aria-label': ariaLabel,
  arrangement = 'leading-label',
  callout,
  children,
  condense,
  disabled = false,
  ghost,
  icon,
  accessLevel: accessLevelProp,
  internal,
  fixtures,
  invalid = false,
  labelAlign,
  labelStretch = false,
  loading = false,
  maxWidth,
  menuArrow,
  minWidth,
  shortcut,
  shortLabel,
  size,
  truncate = false,
  tooltipSide,
  type = 'button',
  variant,
  width,
  ref,
  ...rest
}: PolymorphicComponentProps<Tag, ButtonProps>) {
  const { breakpoint } = useViewport();
  const accessLevel = useAccessLevelContext(accessLevelProp);

  const controlSize = useControlSize(size, 'medium');
  const controlAppearance = useControlAppearance(appearance, 'vivid');
  const controlGhost = useControlGhost(ghost, false);
  const controlWidth = useControlWidth(width);
  const controlMaxWidth = useControlMaxWidth(maxWidth);
  const controlMinWidth = useControlMinWidth(minWidth);
  const controlVariant = useControlVariant(variant, 'primary');

  const controlPreset = getControlPreset(controlSize);

  let generalSize;
  if (controlSize === 'x-large') {
    generalSize = 'large';
  } else if (controlSize === 'large') {
    generalSize = 'medium';
  } else if (controlSize === 'medium') {
    generalSize = 'small';
  } else {
    generalSize = 'x-small';
  }

  let generalVariant;
  if (controlVariant === 'secondary') {
    generalVariant = 'neutral';
  } else if (controlVariant === 'destructive') {
    generalVariant = 'negative';
  } else {
    generalVariant = 'brand';
  }

  // Disable the button during its loading state
  const isDisabled = disabled || loading;

  const hiddenLabel = (condense && breakpoint !== 'desktop') || arrangement === 'hidden-label';

  const fixturesCSS: CSS | undefined =
    fixtures && controlVariant === 'secondary' ? getInteriorVariantStyles(fixtures) : undefined;
  const css = merge(
    fixturesCSS,
    widthCSS(controlWidth),
    maxWidthCSS(controlMaxWidth),
    minWidthCSS(controlMinWidth),
  );

  const buttonRender = (
    <ButtonContainer
      {...rest}
      css={css}
      type={type}
      ref={ref}
      aria-label={
        ariaLabel || (arrangement === 'hidden-label' || condense ? `${children}` : undefined)
      }
      data-ghost={controlGhost}
      appearance={controlGhost ? 'muted' : controlAppearance}
      arrangement={arrangement}
      variant={controlVariant}
      size={controlSize}
      internal={internal || accessLevel === AccessLevel.Internal}
      ghost={controlGhost}
      isDisabled={isDisabled}
      hasMenu={Boolean(menuArrow)}
      disabled={isDisabled}
      invalid={invalid}
      truncate={truncate}
    >
      {loading && (
        <ButtonLoadingContainer>
          <ButtonLoadingIcon />
        </ButtonLoadingContainer>
      )}
      {callout != null && <Callout variant={callout} />}
      <AlignStack
        direction="row"
        justify="center"
        preset={controlPreset}
        end={
          ((shortcut && arrangement !== 'hidden-label') || menuArrow) && (
            <>
              {arrangement !== 'hidden-label' && shortcut && (
                <ButtonShortcut
                  control={controlAppearance !== 'muted'}
                  internal={internal}
                  isLoading={loading}
                  variant={controlVariant === 'secondary' ? 'neutral' : controlVariant}
                  keys={shortcut}
                />
              )}
              {menuArrow && (
                <ButtonMenuArrow
                  isLoading={loading}
                  icon={menuArrow === 'dropdown' ? 'chevron-down' : 'chevrons-vertical'}
                />
              )}
            </>
          )
        }
        width="full"
        gap={getButtonGap(controlSize, breakpoint)}
      >
        <AlignStack
          direction={
            arrangement === 'leading-icon' || arrangement === 'hidden-label' ? 'row' : 'row-reverse'
          }
          align="center"
          justify="center"
          preset={controlPreset}
          start={icon && <ButtonIcon isLoading={loading} icon={icon} />}
          end={
            Boolean((accessLevel && isAccessLevelVisible(accessLevel)) || annotation) && (
              <>
                {accessLevel && isAccessLevelVisible(accessLevel) && (
                  <FeatureBadge size="x-small" arrangement="icon-only" type={accessLevel} />
                )}
                {annotation && (
                  <Badge
                    internal={internal}
                    size={generalSize as BadgeSize}
                    variant={generalVariant as VariantProp}
                    elide={!hiddenLabel}
                  >
                    {annotation}
                  </Badge>
                )}
              </>
            )
          }
          width="full"
          gap={getButtonGap(controlSize, breakpoint)}
        >
          {!hiddenLabel && (
            <ButtonLabel isLoading={loading} stretch={labelStretch}>
              {breakpoint !== 'desktop' && shortLabel ? shortLabel : children}
            </ButtonLabel>
          )}
        </AlignStack>
      </AlignStack>
    </ButtonContainer>
  );

  if (arrangement === 'hidden-label' || (condense && breakpoint !== 'desktop')) {
    return (
      <Tooltip
        contents={
          <AlignStack
            align="center"
            gap={4}
            wrap="nowrap"
            preset="small"
            end={shortcut && <Shortcut keys={shortcut} />}
          >
            <Text>{children}</Text>
          </AlignStack>
        }
        side={tooltipSide}
        sideOffset={2}
        toggle={false}
      >
        {buttonRender}
      </Tooltip>
    );
  }

  return buttonRender;
}
